import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
} from "@mui/material";
import InfoHeader from "../components/InfoHeader";
import { useEffect, useState } from "react";
import Empresa from "../types/negocio/Empresa";
import { empresaService } from "../services/EmpresaService";
import toastService from "../utils/toastService";
import theme from "../themes/theme";
import MetaGlobal from "../types/negocio/MetaGlobal";
import { metaGlobalService } from "../services/MetaGlobalService";
import CardMetasGlobais from "../components/CardMetasGlobais";
import { swotService } from "../services/SwotService";
import Swot from "../types/negocio/Swot";
import { styled } from "@mui/material/styles";
import PlanoAcao from "../types/negocio/PlanoAcao";
import { planoAcaoService } from "../services/PlanoAcaoService";
import { EmptyMessage } from "../components/EmptyMessage";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        paddingTop: 12,
        paddingBottom: 14,
        whiteSpace: "normal",
        wordWrap: "break-word",
        textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        paddingTop: 8,
        paddingBottom: 8,
        textAlign: "center",
    },
}));


const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export default function MapaEstrategico() {
    const [empresa, setEmpresa] = useState<Empresa>({} as Empresa);
    const [isLoadingMetas, setIsLoadingMetas] = useState(true);
    const [isLoadingAcoes, setIsLoadingAcoes] = useState(true);
    const [isLoadingSwot, setIsLoadingSwot] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [metas, setMetas] = useState<MetaGlobal[]>([]);
    const [pontosFortes, setPontosFortes] = useState<Swot[]>([]);
    const [oportunidades, setOportunidades] = useState<Swot[]>([]);
    const [pontosFracos, setPontosFracos] = useState<Swot[]>([]);
    const [ameacas, setAmeacas] = useState<Swot[]>([]);
    const [acoes, setAcoes] = useState<PlanoAcao[]>([]);

    const maxRowsSwot = Math.max(
        pontosFortes.length,
        pontosFracos.length,
        oportunidades.length,
        ameacas.length
    );

    const maxRowsAcoes = Math.max(
        acoes.filter((a) => a.area === "Vendas").length,
        acoes.filter((a) => a.area === "Pessoas").length,
        acoes.filter((a) => a.area === "Finanças").length,
        acoes.filter((a) => a.area === "Processos / Produção").length
    );

    useEffect(() => {
        const fetchAcoes = async () => {
            try {
                setIsLoadingAcoes(true);
                const acoesData = await planoAcaoService.listarTodos();
                setAcoes(acoesData);
            } catch {
                toastService.error("Erro ao carregar ações");
            } finally {
                setIsLoadingAcoes(false);
            }
        };

        fetchAcoes();
    }, []);

    useEffect(() => {
        const fetchMetas = async () => {
            try {
                setIsLoadingMetas(true);
                const fetchedMetas = await metaGlobalService.listarTodos();
                setMetas(fetchedMetas);
            } catch {
                toastService.error("Erro ao carregar metas globais");
            } finally {
                setIsLoadingMetas(false);
            }
        };

        fetchMetas();
    }, []);

    useEffect(() => {
        const fetchEmpresa = async () => {
            try {
                setError(null);
                const data = await empresaService.getEmpresa();
                if (data) {
                    setEmpresa(data);
                }
            } catch (err) {
                console.error("Erro ao carregar empresa:", err);
                setError("Não foi possível carregar os dados da empresa. Por favor, tente novamente.");
                toastService.error("Erro ao carregar dados da empresa");
            }
        };

        fetchEmpresa();
    }, []);

    const areasFixas = ["Vendas", "Finanças", "Processos / Produção", "Pessoas"];
    const metasPorArea = metas.reduce<Record<string, MetaGlobal[]>>((acc, meta) => {
        const area = meta.area;
        if (!acc[area]) {
            acc[area] = [];
        }
        acc[area].push(meta);
        return acc;
    }, {});

    useEffect(() => {
        setIsLoadingSwot(true);
        swotService.obterQuadrante().then((data) => {
            setPontosFortes(data.pontosFortes);
            setPontosFracos(data.pontosFracos);
            setOportunidades(data.oportunidades);
            setAmeacas(data.ameacas);
            setIsLoadingSwot(false);
        }).catch((error) => {
            console.error("Erro ao carregar os dados do SWOT:", error);
            toastService.error("Erro ao carregar os dados do SWOT");
            setIsLoadingSwot(false);
        });
    }, []);

    const getByPontuacao = (items: Swot[]) => {
        return [...items]
            .sort((a, b) => (b.pontuacao || 0) - (a.pontuacao || 0));
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: "stretch"
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Paper
                            elevation={2}
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    py: 3,
                                    textAlign: "center",
                                    backgroundColor: theme.palette.secondary.main,
                                }}
                            >
                                <Typography variant="h5" sx={{ fontWeight: "medium", color: "white" }}>
                                    {empresa.nome}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 3,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    backgroundColor: "white",
                                    flex: 1,
                                }}
                            >
                                <Typography fontSize={18} sx={{ color: "primary.main" }}>
                                    Metas Globais
                                </Typography>

                                {isLoadingMetas  ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                        <CircularProgress size={32} />
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                        {areasFixas.map((area) => (
                                            <CardMetasGlobais
                                                key={area}
                                                area={area}
                                                metas={metasPorArea[area] || []}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                            }}
                        >
                            <Typography fontSize={14} sx={{ color: "#607C90" }}>
                                Missão
                            </Typography>
                            <Typography fontSize={16}>
                                {empresa.missao}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                            }}
                        >
                            <Typography fontSize={14} sx={{ color: "#607C90" }}>
                                Visão
                            </Typography>
                            <Typography fontSize={16}>
                                {empresa.visao}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                            }}
                        >
                            <Typography fontSize={14} sx={{ color: "#607C90" }}>
                                Valores
                            </Typography>
                            <Typography fontSize={16}>
                                {empresa.valores}
                            </Typography>
                        </Paper>
                    </Box>
                </Box>

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: 'white',
                            pt: 2,
                            textAlign: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Typography fontSize={20} fontWeight="semibold">
                            Análise SWOT
                        </Typography>
                    </Box>

                    <TableContainer>
                        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Pontos Fortes</StyledTableCell>
                                    <StyledTableCell>Pontos Fracos</StyledTableCell>
                                    <StyledTableCell>Oportunidades</StyledTableCell>
                                    <StyledTableCell>Ameaças</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingSwot ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={32} />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Array.from({ length: maxRowsSwot }).map((_, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell>
                                                {getByPontuacao(pontosFortes)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {getByPontuacao(pontosFracos)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {getByPontuacao(oportunidades)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {getByPontuacao(ameacas)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: 'white',
                            pt: 2,
                            textAlign: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Typography fontSize={20} fontWeight="semibold">
                            Ações Estratégicas
                        </Typography>
                    </Box>

                    <TableContainer>
                        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Vendas</StyledTableCell>
                                    <StyledTableCell>Pessoas</StyledTableCell>
                                    <StyledTableCell>Finanças</StyledTableCell>
                                    <StyledTableCell>Processos / Produção</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingAcoes ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={32} />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Array.from({ length: maxRowsAcoes }).map((_, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Vendas")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Pessoas")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Finanças")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Processos / Produção")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    )
}